// #region viewAsBoot
/**
 * "View as customer" runs in its own tab. This module is imported FIRST in index.tsx, before
 * anything reads the login from localStorage, and does two things:
 *
 * 1. Arriving at /view-as?k=<nonce>: picks up the session the lead page parked under that
 *    nonce, moves it into this tab's sessionStorage, and lands on the dashboard.
 * 2. While a view is active in this tab: localStorage 'user' / 'authToken' read from and write
 *    to this tab's session instead. The rep's own login (shared by every tab) is never touched,
 *    and a logout here only ends the view.
 */

export interface ViewAsMeta {
    sid: number;
    mode: 'read' | 'edit';
    rep_user_id: number;
    rep_name: string;
    lead_id: number | null;
    brand_id: number;
    brand_name: string;
}

export interface ViewAsState {
    token: string;
    user: Record<string, unknown>;
    view_as: ViewAsMeta;
    expires_at: string;
}

const STATE_KEY = 'wallmates.viewAs';
export const VIEW_AS_PENDING_PREFIX = 'wallmates.viewAs.pending.';
export const VIEW_AS_ENDED_PATH = '/view-as/ended';

const realGet = Storage.prototype.getItem;
const realSet = Storage.prototype.setItem;
const realRemove = Storage.prototype.removeItem;

const readState = (): ViewAsState | null => {
    try {
        const raw = realGet.call(window.sessionStorage, STATE_KEY);
        return raw ? (JSON.parse(raw) as ViewAsState) : null;
    } catch {
        return null;
    }
};

const writeState = (state: ViewAsState | null): void => {
    try {
        if (state) realSet.call(window.sessionStorage, STATE_KEY, JSON.stringify(state));
        else realRemove.call(window.sessionStorage, STATE_KEY);
    } catch { /* private mode: the view simply won't survive a reload */ }
};

/** The view active in this tab, or null in a normal tab. */
export const viewAsState = (): ViewAsState | null => readState();

/** Leaves the view: forgets it in this tab and shows the "view ended" page. */
export const leaveViewAs = (): void => {
    writeState(null);
    window.location.replace(VIEW_AS_ENDED_PATH);
};

const pickUpPending = (): void => {
    if (window.location.pathname !== '/view-as') return;
    const nonce = new URLSearchParams(window.location.search).get('k');
    if (!nonce) return;
    let raw: string | null = null;
    try {
        raw = realGet.call(window.localStorage, VIEW_AS_PENDING_PREFIX + nonce);
        realRemove.call(window.localStorage, VIEW_AS_PENDING_PREFIX + nonce);
    } catch { /* storage blocked */ }
    if (!raw) {
        window.history.replaceState(null, '', VIEW_AS_ENDED_PATH);
        return;
    }
    writeState(JSON.parse(raw) as ViewAsState);
    window.history.replaceState(null, '', '/');
};

const redirectLogin = (): void => {
    Storage.prototype.getItem = function (this: Storage, key: string): string | null {
        const state = this === window.localStorage ? readState() : null;
        if (state && key === 'authToken') return state.token;
        if (state && key === 'user') return JSON.stringify(state.user);
        return realGet.call(this, key);
    };
    Storage.prototype.setItem = function (this: Storage, key: string, value: string): void {
        const state = this === window.localStorage ? readState() : null;
        if (state && key === 'authToken') { writeState({ ...state, token: value }); return; }
        if (state && key === 'user') { writeState({ ...state, user: JSON.parse(value) }); return; }
        realSet.call(this, key, value);
    };
    Storage.prototype.removeItem = function (this: Storage, key: string): void {
        const state = this === window.localStorage ? readState() : null;
        // The app logs out on a 401 — here that means the view ended (expired or revoked).
        if (state && (key === 'authToken' || key === 'user')) {
            if (key === 'authToken') setTimeout(leaveViewAs, 0);
            return;
        }
        realRemove.call(this, key);
    };
};

pickUpPending();
if (readState()) redirectLogin();
